import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Button,
  Flex,
  Heading,
  Stack,
  Image,
  Alert,
  AlertIcon,
  Text,
} from '@chakra-ui/react';
import { FcGoogle } from 'react-icons/all';
import { setAndClear, getBackendURL, apiFetch } from '../../utils/utils';
import { useDispatch, useSelector } from 'react-redux';
import { setUser } from '../../actions/user-actions';
import nexusAuth from '../../auth';
import { generatePortalToken } from '../../utils/portalToken';

function LoginPage() {
  const dispatch = useDispatch();
  const user = useSelector(state => state.userState.user);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [inProgress, setInProgress] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (user) {
      navigate('/home');
    }
  }, [user, navigate]);

  const handleGoogleLogin = async () => {
    setError('');
    setInProgress(true);

    try {
      const result = await nexusAuth.loginWithGoogle();
      const idToken = await result.user.getIdToken();
      const portalToken = await generatePortalToken(
        'admin',
        'ADMIN',
        process.env.REACT_APP_PORTAL_SECRET
      );

      const response = await apiFetch(`${getBackendURL()}/auth/google-login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ idToken, portalToken }),
      });

      const data = await response.json();

      if (response.status === 403 || response.status === 401) {
        setAndClear(data.message, setError);
        setInProgress(false);
        return;
      }

      if (data.message === 'USER_NOT_FOUND') {
        setAndClear('No admin account found for this email. Contact an administrator.', setError);
        setInProgress(false);
        return;
      }

      if (data.user) {
        setAndClear('Login successful!', setSuccess);
        dispatch(setUser(data.user));
        navigate('/home');
      } else {
        setAndClear(data.message || 'Login failed', setError);
      }
    } catch (err) {
      console.error('Login error:', err);
      setAndClear('Something went wrong. Please try again.', setError);
    } finally {
      setInProgress(false);
    }
  };

  return (
    <Stack
      display={'flex'}
      minH={'100vh'}
      direction={{ base: 'column', md: 'row' }}
    >
      <Flex p={8} flex={1} align={'center'} justify={'center'}>
        <Stack spacing={4} w={'full'} maxW={'md'}>
          <Heading fontSize={'2xl'}>Admin Portal</Heading>
          <Text color="gray.500">
            Sign in with your Google account to access the admin dashboard.
          </Text>
          <Alert status="success" display={success ? 'flex' : 'none'}>
            <AlertIcon />
            {success}
          </Alert>
          <Alert status="error" display={error ? 'flex' : 'none'}>
            <AlertIcon />
            {error}
          </Alert>
          <Stack spacing={6}>
            <Button
              w="full"
              variant="outline"
              leftIcon={<FcGoogle />}
              onClick={handleGoogleLogin}
              disabled={inProgress}
              size="lg"
            >
              {inProgress ? 'Signing in...' : 'Sign in with Google'}
            </Button>
          </Stack>
        </Stack>
      </Flex>
      <Flex flex={1}>
        <Image
          alt={'Login Image'}
          objectFit={'cover'}
          src={
            'https://images.unsplash.com/photo-1486312338219-ce68d2c6f44d?ixid=MXwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHw%3D&ixlib=rb-1.2.1&auto=format&fit=crop&w=1352&q=80'
          }
        />
      </Flex>
    </Stack>
  );
}

export default LoginPage;
